/**
 * Client wire contract for the help agent's SSE stream
 * (`POST /v2/help/conversations/:id/messages`, backend
 * `backend/web/src/help-agent/`).
 *
 * The backend streams the kg-agent V1 envelope (`data: {type, content,
 * ...}` frames terminated by `event: done`). This parser keeps only what
 * the help widget renders and is deliberately tolerant: malformed JSON
 * and unknown frame types return `null` so the backend can add frame
 * types without breaking deployed clients.
 */

export type HelpAction =
  | { type: 'navigate'; route: string }
  | { type: 'start_tour'; tourId: string }

export type HelpAgentFrame =
  | { type: 'text'; delta: string }
  | { type: 'tool_activity'; toolName: string }
  | { type: 'help_action'; action: HelpAction }
  | { type: 'error'; message?: string }

/** App-relative paths only — reject absolute/protocol-relative URLs. */
export function isSafeAppPath(route: unknown): route is string {
  return (
    typeof route === 'string' &&
    route.startsWith('/') &&
    !route.startsWith('//') &&
    !route.includes('://')
  )
}

function parseHelpAction(raw: unknown): HelpAction | null {
  if (!raw || typeof raw !== 'object') return null
  const action = raw as { type?: unknown; route?: unknown; tour_id?: unknown }
  if (action.type === 'navigate' && isSafeAppPath(action.route)) {
    return { type: 'navigate', route: action.route }
  }
  if (action.type === 'start_tour' && typeof action.tour_id === 'string' && action.tour_id.trim() !== '') {
    return { type: 'start_tour', tourId: action.tour_id.trim() }
  }
  return null
}

export function parseHelpAgentFrame(data: string): HelpAgentFrame | null {
  if (!data || data === '[DONE]') return null

  let raw: unknown
  try {
    raw = JSON.parse(data)
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object') return null
  const frame = raw as Record<string, unknown>

  switch (frame.type) {
    case 'text':
      return typeof frame.content === 'string' && frame.content.length > 0
        ? { type: 'text', delta: frame.content }
        : null
    case 'tool_call':
      return typeof frame.tool_name === 'string'
        ? { type: 'tool_activity', toolName: frame.tool_name }
        : null
    case 'help_action': {
      const action = parseHelpAction(frame.action)
      return action ? { type: 'help_action', action } : null
    }
    case 'error':
      return {
        type: 'error',
        ...(typeof frame.message === 'string' ? { message: frame.message } : {}),
      }
    default:
      return null
  }
}
